import { Component, OnInit, OnDestroy, HostListener, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MatDatepicker } from '@angular/material/datepicker';

import { HttpService } from '@core/services/http.service';
import { forkJoin, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs';
import { ConfirmRegistrationModalComponent } from '../confirm-registration-modal/confirm-registration-modal.component';
import { ModalDismissReasons, NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { RegistrationService } from '../registration.service';
import { AccountCreationModalComponent } from '../account-creation-modal/account-creation-modal.component';
import { ModalComponent } from '../../shared/custom-component/modal/modal.component';
import { modalButtonTitles, ModalConfig, modalContent, modalHeading } from '../../shared/constants/modal.model';
import { DOCUMENT_CONTENT } from '../../shared/constants/document-content.constants';
import { MatDialog } from '@angular/material/dialog';
import { ContentManagementModalComponent } from '@app/systemconfiguration/content-management/modal/content-management-modal.component';
import { ROMAN_SUFFIX_MAP } from '../../shared/constants/registration-constants';
import { LocalService } from '@app/@core/services/local.service';
import { patternMatch } from '../../shared/constants/validators.model';

@Component({
  standalone: false,
  selector: 'app-registration-form',
  templateUrl: './registration-form.component.html',
  styleUrls: ['./registration-form.component.scss']
})
export class RegistrationFormComponent implements OnInit, OnDestroy {
  private formatPhoneNumberValue(input: string): string {
    if (!input) return '';
    // Block alphabetic input
    input = input.replace(/[a-zA-Z]/g, '');
    // If starts with '+1', enforce '+1 (XXX) XXX XXXX' format
    if (input.startsWith('+1')) {
      if (input.length > 2 && input[2] !== ' ') {
        input = '+1 ' + input.slice(2);
      }
      let numberPart = input.startsWith('+1 ') ? input.slice(3) : input.slice(2);
      let digits = numberPart.replace(/\D/g, '');
            // Strip repeated country code: e.g. user pasted 18XXXXXXXXX after field already had "+1 "
      if (digits.length === 11 && digits.startsWith('1')) {
        digits = digits.substring(1);
      }
      if (digits.length > 10) digits = digits.substring(0, 10);
      let formatted = digits;
      if (digits.length > 6) {
        formatted = `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
      } else if (digits.length > 3) {
        formatted = `(${digits.substring(0, 3)}) ${digits.substring(3)}`;
      } else if (digits.length > 0) {
        formatted = `(${digits}`;
      } else {
        formatted = '';
      }
      return input.startsWith('+1 ') ? '+1 ' + formatted : '+1' + formatted;
    }
    // If starts with '(', enforce '(XXX) XXX XXXX' format
    if (input.startsWith('(')) {
      let digits = input.replace(/\D/g, '');
      if (digits.length > 10) digits = digits.substring(0, 10);
      let formatted = digits;
      if (digits.length > 6) {
        formatted = `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
      } else if (digits.length > 3) {
        formatted = `(${digits.substring(0, 3)}) ${digits.substring(3)}`;
      } else if (digits.length > 0) {
        formatted = `(${digits}`;
      } else {
        formatted = '';
      }
      return formatted;
    }
    // If only digits, format as '(XXX) XXX XXXX'
    if (/^\d+$/.test(input)) {
      let digits = input.substring(0, 10);
      let formatted = digits;
      if (digits.length > 6) {
        formatted = `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
      } else if (digits.length > 3) {
        formatted = `(${digits.substring(0, 3)}) ${digits.substring(3)}`;
      } else if (digits.length > 0) {
        formatted = `(${digits}`;
      } else {
        formatted = '';
      }
      return formatted;
    }
    // Allow '+', '+1', '+1 ' as valid prefixes
    if (input === '+' || input === '+1' || input === '+1 ') {
      return input;
    }
    // For any other input, clear the field
    return '';
  }
  onFirstNameInput(event: any) {
    let value = event.target.value;
    value = value.replace(/[^A-Za-z\-' ]/g, '');
    value = value.substring(0, 30);
    this.registrationForm.get('firstName')?.setValue(this.toTitleCase(this.normalizeSpaces(value)), { emitEvent: false });
  }

  @HostListener('document:click', ['$event']) onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.tooltip-container')) {
      this.showSSNTooltip = false;
      this.emailTooltip = false;
      this.recoveryEmailTooltip = false;
    }
  }

  showSSNTooltip = false;
  tooltipAnnouncement = '';
  // SSNDetected and ITINDetected come from your existing logic

  toggleTooltip(event?:Event): void {
    event?.stopPropagation();
    this.showSSNTooltip = !this.showSSNTooltip;
    if (this.showSSNTooltip) {
      this.announceTooltip();
    }
  }

  openTooltip(): void {
    this.showSSNTooltip = true;
    this.announceTooltip();
  }

  closeTooltip(): void {
    this.showSSNTooltip = false;
    this.tooltipAnnouncement = '';
  }

  announceTooltip(): void {
    // Clear first, then set the message to force screen reader announcement
    this.tooltipAnnouncement = '';
    setTimeout(() => {
      this.tooltipAnnouncement = this.genericMessage['G0003'] || 'SSN/ITIN information';
    }, 50);
  }

  onInfoKeydown(event: KeyboardEvent): void {
    // Space or Enter toggles; Escape closes
    const key = event.key;
    if (key === ' ' || key === 'Spacebar' || key === 'Enter') {
      event.preventDefault();
      this.toggleTooltip();
    } else if (key === 'Escape') {
      this.closeTooltip();
      (event.target as HTMLElement)?.blur();
    }
  }

  // Email tooltip methods
  toggleEmailTooltip(event?:Event): void {
    event?.stopPropagation();
    this.emailTooltip = !this.emailTooltip;
    if (this.emailTooltip) {
      this.announceEmailTooltip();
    } else {
      this.emailTooltipAnnouncement = '';
    }
  }

  openEmailTooltip(): void {
    this.emailTooltip = true;
    this.announceEmailTooltip();
  }

  closeEmailTooltip(): void {
    this.emailTooltip = false;
    this.emailTooltipAnnouncement = '';
  }

  announceEmailTooltip(): void {
    this.emailTooltipAnnouncement = '';
    setTimeout(() => {
      this.emailTooltipAnnouncement = this.genericMessage['G0002'] || 'Email information';
    }, 50);
  }

  onEmailInfoKeydown(event: KeyboardEvent): void {
    const key = event.key;
    if (key === ' ' || key === 'Spacebar' || key === 'Enter') {
      event.preventDefault();
      this.toggleEmailTooltip();
    } else if (key === 'Escape') {
      this.closeEmailTooltip();
      (event.target as HTMLElement)?.blur();
    }
  }

  // Recovery Email tooltip methods
  toggleRecoveryEmailTooltip(event?:Event): void {
    event?.stopPropagation();
    this.recoveryEmailTooltip = !this.recoveryEmailTooltip;
    if (this.recoveryEmailTooltip) {
      this.announceRecoveryEmailTooltip();
    } else {
      this.recoveryEmailTooltipAnnouncement = '';
    }
  }

  openRecoveryEmailTooltip(): void {
    this.recoveryEmailTooltip = true;
    this.announceRecoveryEmailTooltip();
  }

  closeRecoveryEmailTooltip(): void {
    this.recoveryEmailTooltip = false;
    this.recoveryEmailTooltipAnnouncement = '';
  }

  announceRecoveryEmailTooltip(): void {
    this.recoveryEmailTooltipAnnouncement = '';
    setTimeout(() => {
      this.recoveryEmailTooltipAnnouncement = this.genericMessage['G0002'] || 'Recovery Email information';
    }, 50);
  }

  onRecoveryEmailInfoKeydown(event: KeyboardEvent): void {
    const key = event.key;
    if (key === ' ' || key === 'Spacebar' || key === 'Enter') {
      event.preventDefault();
      this.toggleRecoveryEmailTooltip();
    } else if (key === 'Escape') {
      this.closeRecoveryEmailTooltip();
      (event.target as HTMLElement)?.blur();
    }
  }

  onMiddleNameInput(event: any) {
    let value = event.target.value;
    value = value.replace(/[^A-Za-z\-' ]/g, '');
    value = value.substring(0, 30);
    this.registrationForm.get('middleName')?.setValue(this.toTitleCase(this.normalizeSpaces(value)), { emitEvent: false });
  }

  onLastNameInput(event: any) {
    let value = event.target.value;
    value = value.replace(/[^A-Za-z\-' ]/g, '');
    value = value.substring(0, 30);
    this.registrationForm.get('lastName')?.setValue(this.toTitleCase(this.normalizeSpaces(value)), { emitEvent: false });
  }

  private toTitleCase(text: string): string {
    if (!text) return '';
    return text.toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  }

  normalizeSpaces(value: string) {
    // Replace multiple spaces with a single space and trim leading/trailing spaces
    value = value.replace(/\s+/g, ' ');
    return value;
  }

  getSuffixAriaLabel(value: string | null | undefined): string {
    if (!value) {
      return '';
    }
    const normalizedValue = value.trim().toUpperCase();
    return ROMAN_SUFFIX_MAP[normalizedValue] || value;
  }

  checkForTrailingSpace(event: any, controlName: string) {
    let value = event.target.value;
    this.registrationForm.get(controlName)?.setValue(value.trimEnd(), { emitEvent: false });
  }

  emailTooltip = false;
  recoveryEmailTooltip = false;
  emailTooltipAnnouncement = '';
  recoveryEmailTooltipAnnouncement = '';
  registrationForm: FormGroup;
  showSSN = true;
  ssnFocused = false;
  SSNDetected = false;
  ITINDetected = false;
  idTypeDetected: 'SSN' | 'ITIN' | null = null;
  displayedSSN = '';
  genderList: any[] = [];
  genderClearedAnnouncement = '';
  
  @ViewChild('genderField', { static: false }) genderField: ElementRef<HTMLSelectElement>;
  
  ethnicityList: any[] = [];
  raceList: any[] = [];
  suffixList: any[] = [];
  genericMessages: any[] = [];
  placeholderMessages: any[] = [];
  errorMessages: any[] = [];
  toastMessages: any[] = [];
  modalRef: NgbModalRef;
  customModal: NgbModalRef;
  userAgreementContent: string = '';
  SSNError: boolean = false;
  emailError: boolean = false;
  recoveryEmailError: boolean = false;
  phoneError: boolean = false;
  closeResult = '';
  isEmailFocused: boolean = false;
  emailSentMessage: boolean = false;
  currentDate: string = new Date().toISOString().split('T')[0];
  errorMessage: any = {};
  toastMessage: any = {};
  genericMessage: any = {};
  placeholderMessage: any = {};
  ssnExists: boolean = false;
  emailExists: boolean = false;
  phoneExists: boolean = false;
  recoveryEmailExists: boolean = false;
  isPhoneFocused: boolean = false;
  isRecoveryEmailFocused: boolean = false;
  isSSNFocused: boolean = false;
  minAge: number = 14; // Default fallback
  maxAge: number = 55; // Default fallback
  private destroy$ = new Subject<void>();

  get currentToastMessage(): string {
    if (this.SSNError && this.idTypeDetected === 'SSN') {
      return this.toastMessage['T0001'];
    }
    if (this.SSNError && this.idTypeDetected === 'ITIN') {
      return this.toastMessage['T0016'];
    }
    if (this.emailError && !this.recoveryEmailError && !this.phoneError && !this.SSNError) {
      return this.toastMessage['T0002'];
    }
    if (!this.emailError && this.recoveryEmailError && !this.phoneError && !this.SSNError) {
      return this.toastMessage['T0003'];
    }
    if (this.phoneError && !this.emailError && !this.recoveryEmailError && !this.SSNError) {
      return this.toastMessage['T0004'];
    }
    return '';
  }

    constructor(
      private readonly fb: FormBuilder,
      private readonly router: Router,
      private readonly httpService: HttpService,
      private readonly ngModal: NgbModal,
      private readonly registrationService: RegistrationService,
      private readonly matDialog: MatDialog
    ) {}
  ngOnInit(): void {
    this.loadDropdownData();
    this.loadAgeValidationRules();
    this.createForm();
    
    this.clearFormData();
    
    this.registrationForm.get('ssn')?.valueChanges.subscribe(() => {
      this.updateDisplayedSSN();
      this.ssnExists = false;
    });
    
    this.registrationForm.get('email')?.valueChanges.subscribe(() => {
      this.emailExists = false;
      this.validateRecoveryEmail();
    });
    
    this.registrationForm.get('recovery_email')?.valueChanges.subscribe(() => {
      this.recoveryEmailExists = false;
      this.validateRecoveryEmail();
    });
    
    this.registrationForm.get('phoneNumber')?.valueChanges.subscribe(() => {
      this.phoneExists = false;
    });
    
    this.updateDisplayedSSN();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearFormData();
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnloadHandler(event: any) {
    this.clearFormData();
  }

  @HostListener('window:popstate', ['$event'])
  onPopState(event: any) {
    this.clearFormData();
  }
  private async loadDropdownData() {
    forkJoin([
      this.httpService.get('/domain/lookup?requestType=GENDER_TYPES'),
      this.httpService.get('/domain/lookup?requestType=ETHICITY_TYPES'),
      this.httpService.get('/domain/lookup?requestType=RACE_CODES'),
      this.httpService.get('/domain/lookup?requestType=SUFFIX'),
      this.httpService.get('/domain/lookup?requestType=MESSAGES'),
      this.httpService.get('/domain/lookup?requestType=TOAST MESSAGES'),
      this.httpService.get('/domain/lookup?requestType=GENERIC MESSAGES'),
      this.httpService.get('/domain/lookup?requestType=PLACEHOLDER MESSAGES'),
      this.registrationService.getUserAgreementContent(500, '4', 'EDUCATOR'),
    ]).subscribe({
      next: (item: any) => {
        this.genderList = item[0].data.sort((a, b) => a.value.localeCompare(b.value));
        this.ethnicityList = item[1].data.sort((a, b) => a.value.localeCompare(b.value));
        
        this.raceList = item[2].data;
        const preferNotToAnswerIndex = this.raceList.findIndex(item => 
          item.value === 'Prefer Not to Answer' || 
          item.value === 'Prefer not to answer' || 
          item.value.toLowerCase().includes('prefer not to answer')
        );
        
        if (preferNotToAnswerIndex !== -1) {
          const preferNotToAnswerItem = this.raceList.splice(preferNotToAnswerIndex, 1)[0];
          this.raceList.sort((a, b) => a.value.localeCompare(b.value));
          this.raceList.push(preferNotToAnswerItem);
        } else {
          this.raceList.sort((a, b) => a.value.localeCompare(b.value));
        }
        
        this.suffixList = item[3].data;
        const noneIndex = this.suffixList.findIndex(item => item.value === 'None');
        if (noneIndex !== -1) {
          const noneItem = this.suffixList.splice(noneIndex, 1)[0];
          this.suffixList.push(noneItem);
        }
        
        this.errorMessages = item[4].data;
        this.toastMessages = item[5].data;
        this.genericMessages = item[6].data;
        this.placeholderMessages = item[7].data;
        this.errorMessage = {};
        this.errorMessages.forEach(message => {
          if (message.key && message.value !== undefined) {
            this.errorMessage[message.key] = message.value;
          }
        });
        window.localStorage.setItem('errorMessages', JSON.stringify(this.errorMessages));
        this.toastMessage = {};
        this.toastMessages.forEach(message => {
          if (message.key && message.value !== undefined) {
            this.toastMessage[message.key] = message.value;
          }
        });
        window.localStorage.setItem('toastMessages', JSON.stringify(this.toastMessages));
        console.log(item);
        this.genericMessage = {};
        this.genericMessages.forEach(message => {
          if (message.key && message.value !== undefined) {
            this.genericMessage[message.key] = message.value;
          }
        });
        window.localStorage.setItem('genericMessages', JSON.stringify(this.genericMessages));
        this.placeholderMessage = {};
        this.placeholderMessages.forEach(message => {
          if (message.key && message.value !== undefined) {
            this.placeholderMessage[message.key] = message.value;
          }
        });
        window.localStorage.setItem('placeholderMessages', JSON.stringify(this.placeholderMessages));

        // Store user agreement content from lookup
        if (item[8]?.data?.length > 0) {
          const optionalData = item[8].data[0].optionaldata;
          if (optionalData) {
            try {
              const parsed = typeof optionalData === 'string' ? JSON.parse(optionalData) : optionalData;
              this.userAgreementContent = parsed?.content || '';
            } catch {
              this.userAgreementContent = optionalData;
            }
          }
        }

      },
      error: error => {
        console.error('Error loading dropdown data:', error);
      }
    });
  }
  createForm() {
    this.registrationForm = this.fb.group({
      firstName: [
      '',
      [
      Validators.required,
      Validators.pattern("^[A-Za-z]+([-' ][A-Za-z]+)*\\s*$"),
      Validators.minLength(2),
      Validators.maxLength(30)
      ]
      ],
      middleName: [
      '',
      [Validators.pattern("^[A-Za-z]*([-' ][A-Za-z]+)*\\s*$"), Validators.maxLength(30)]
      ],
      lastName: [
      '',
      [
      Validators.required,
      Validators.pattern("^[A-Za-z]+([-' ][A-Za-z]+)*\\s*$"),
      Validators.minLength(2),
      Validators.maxLength(30)
      ]
      ],
      suffix: [null],
      dob: ['', [Validators.required, this.validateDate.bind(this)]],
      gender: [null, Validators.required],
      race: [null, Validators.required],
      ethnicity: [null, Validators.required],
      ssn: ['', [Validators.required, this.validateSSNITIN.bind(this)]],
      idType: ['SSN'], // Default type is SSN
      email: [
      '',
      [
        Validators.required,
        Validators.pattern(patternMatch.registrationEmail)
      ]
      ],
      recovery_email: [
      '',
      [
        Validators.pattern(patternMatch.registrationEmail)
      ]
      ],
      phoneNumber: [
      '',
      [
        Validators.required,
        Validators.pattern('^(\\+1[ ]?)?\\(\\d{3}\\)[ ]?\\d{3}-\\d{4}$'),
        Validators.maxLength(17),
        (control) => {
          if (!control.value) return null;
          let digits = control.value.replace(/\D/g, '');
          if (digits.length === 11 && digits.startsWith('1')) {
            digits = digits.substring(1);
          }
          if (digits.length === 10 && /^0+$/.test(digits)) {
            return { allZeros: true };
          }
          return null;
        }
      ]
      ],
      terms: [false, Validators.requiredTrue],
      consent: [false, Validators.requiredTrue]
    });
  }
  formatPhoneNumber(event: any) {
    const inputEl = event.target;
    let value = inputEl.value;   
    const inputType = event.inputType; 
    // "deleteContentBackward", "insertText", etc.
    const isDeleting = inputType && inputType.includes('delete');
    //Allow deletion of +1 fully
    if (isDeleting) {
      this.registrationForm.get('phoneNumber')?.setValue(value, { emitEvent: false });
      return;
    }
    if (!value.startsWith('+1') && value !== '+') {
      value = '+1 ' + value.replace(/^\+/, '');
    }  
    const formatted = this.formatPhoneNumberValue(value); 
    inputEl.value = formatted;
    this.registrationForm.get('phoneNumber')?.setValue(formatted, { emitEvent: false });
  }

  onSSNFocus() {
    this.ssnFocused = true;
    this.showSSN = true;
    this.updateDisplayedSSN();
  }

  onSSNBlur() {
    this.ssnFocused = false;
    this.showSSN = false;
    this.updateDisplayedSSN();
    
    const value = this.registrationForm.get('ssn')?.value || '';
    const digits = value.replace(/\D/g, '');
    
    if (digits.length === 9) {
      this.checkSSN();
    } else {
      this.SSNDetected = false;
      this.ITINDetected = false;
    }
  }
  toggleSSN(event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.showSSN = !this.showSSN;
    this.updateDisplayedSSN();
  }

  openDatePicker(event?: Event) {
  if (event) {
    event.stopPropagation();
    // DO NOT call event.preventDefault() - it breaks iOS touch events
  }

  const dobInput = document.getElementById('input_registration_form_date_of_birth');
    if (!dobInput) {
      return;
    }

  const rect = dobInput.getBoundingClientRect();

  const dateInput = document.createElement('input');
  dateInput.type = 'date';
  dateInput.style.position = 'absolute';
  dateInput.style.top = (rect.top + window.scrollY + 5) + 'px';
  dateInput.style.bottom = (rect.bottom + window.scrollY + 5) + 'px';
  dateInput.style.left = rect.left + 'px';
  dateInput.style.width = '120px'; // Make it visible for iOS
  dateInput.style.height = '35px';
  dateInput.style.opacity = '0.01'; // Almost invisible but clickable
  dateInput.style.border = 'none';
  dateInput.style.zIndex = '9999';
  dateInput.style.pointerEvents = 'auto'; // Allow interaction
  dateInput.max = this.currentDate;

  const currentValue = this.registrationForm.get('dob')?.value;
  if (currentValue && currentValue.includes('/')) {
    const parts = currentValue.split('/');
    if (parts.length === 3 && parts[2].length === 4) {
      const month = parts[0].padStart(2, '0');
      const day = parts[1].padStart(2, '0');
      const year = parts[2];
      dateInput.value = `${year}-${month}-${day}`;
    }
  }

  document.body.appendChild(dateInput);
  const cleanup = () => {
    if (document.body.contains(dateInput)) {
      dateInput.removeEventListener('change', changeHandler);
      dateInput.removeEventListener('blur', blurHandler);
      document.body.removeChild(dateInput);
    }
  };

  const changeHandler = (changeEvent: any) => {
    const selectedDate = changeEvent.target.value;
    if (selectedDate) {
        try {
          const dateParts = selectedDate.split('-');
          if (dateParts.length === 3) {
            const year = dateParts[0];
            const month = dateParts[1];
            const day = dateParts[2];
      const formattedDate = `${month}/${day}/${year}`;
            
      this.registrationForm.get('dob')?.setValue(formattedDate);
      this.registrationForm.get('dob')?.markAsTouched();
      this.registrationForm.get('dob')?.updateValueAndValidity();
          }
        } catch (error) {
          console.error('Error formatting date:', error);
        }
    }
    cleanup();
    
    // Move focus to next field (Gender) after date selection
    setTimeout(() => {
      if (this.genderField?.nativeElement) {
        this.genderField.nativeElement.focus();
      }
    }, 150);
  };

  const blurHandler = () => {
    setTimeout(cleanup, 100);
  };

  dateInput.addEventListener('change', changeHandler);
  dateInput.addEventListener('blur', blurHandler);

  // CRITICAL: For iOS, must call focus/click synchronously (no setTimeout)
  dateInput.focus();
  
  // Try showPicker first (modern browsers), then click (iOS fallback)
  if ('showPicker' in dateInput) {
    try {
      (dateInput as any).showPicker();
    } catch {
      (dateInput as HTMLInputElement).click();
    }
  } else {
    (dateInput as HTMLInputElement).click();
  }
}

  formatDateInput(event: any) {
    let value = event.target.value.replace(/\D/g, ''); // Remove non-digits
    
    if (value.length > 8) {
      value = value.substring(0, 8);
    }
    
    let formatted = value;
    if (value.length >= 5) {
      formatted = `${value.substring(0, 2)}/${value.substring(2, 4)}/${value.substring(4, 8)}`;
    } else if (value.length >= 3) {
      formatted = `${value.substring(0, 2)}/${value.substring(2, 4)}`;
    } else if (value.length >= 1) {
      formatted = value.substring(0, 2);
    }
    
    this.registrationForm.get('dob')?.setValue(formatted, { emitEvent: false });
  }
  validateSSNITIN(control: any) {
    const value = control.value || '';
    const digits = value.replace(/\D/g, '');
 
    // Must be exactly 9 digits long
    if (!digits || digits.length !== 9) {
      return { pattern: true };
    }
 
    // Common validations for both SSN and ITIN
   
    // No part may be all zeros: first three, middle two, or last four
    if (digits.substring(0, 3) === '000' || // First three digits cannot be 000
        digits.substring(3, 5) === '00' ||  // Middle two digits cannot be 00
        digits.substring(5, 9) === '0000') { // Last four digits cannot be 0000
      return { pattern: true };
    }
   
    // No repeated digits - cannot consist of all repeated digits
    if (/^(\d)\1{8}$/.test(digits)) {
      return { pattern: true };
    }
   
    // Check if it's an ITIN or SSN based on first digit
    const firstDigit = digits.charAt(0);
   
    if (firstDigit === '9') {
      // ITIN validation - must start with 9
     
      
      const middleDigits = parseInt(digits.substring(3, 5));
      const validRanges =
        (middleDigits >= 50 && middleDigits <= 65) ||
        (middleDigits >= 70 && middleDigits <= 88) ||
        (middleDigits >= 90 && middleDigits <= 92) ||
        (middleDigits >= 94 && middleDigits <= 99);
       
      if (!validRanges) {
        return { pattern: true };
      }
    }
    else {
      // SSN validation
     
      // SSNs cannot begin with 900-999 (reserved for ITINs)
      const firstThreeDigits = parseInt(digits.substring(0, 3));
      if (firstThreeDigits >= 900 && firstThreeDigits <= 999) {
        return { pattern: true };
      }
     
      // The first three digits cannot be 666
      if (digits.substring(0, 3) === '666') {
        return { pattern: true };
      }
     
      // SSNs from 987-65-4320 to 987-65-4329 are invalid (assigned for use in Media)
      if (digits.substring(0, 6) === '987654' &&
          parseInt(digits.substring(6)) >= 320 &&
          parseInt(digits.substring(6)) <= 329) {
        return { pattern: true };
      }
     
      // Check for specific invalid SSN: 078-05-1120
      if (digits === '078051120') {
        return { pattern: true };
      }
    }
   
    return null;
  }

  onSSNInput(event: any) {
    let value = event.target.value.replace(/\D/g, '');
    if (value.length > 9) value = value.substring(0, 9);

    // Format as XXX-XX-XXXX
    let formatted = value;
    if (value.length >= 6) {
      formatted = `${value.substring(0, 3)}-${value.substring(3, 5)}-${value.substring(5, 9)}`;
    } else if (value.length >= 4) {
      formatted = `${value.substring(0, 3)}-${value.substring(3, 5)}`;
    } else if (value.length >= 1) {
      formatted = value.substring(0, 3);
    }

    this.registrationForm.get('ssn')?.setValue(formatted, { emitEvent: false });
    this.updateDisplayedSSN();
    
    if (value.length > 0) {
      const firstDigit = value.charAt(0);
      if (firstDigit === '9') {
        this.ITINDetected = true;
        this.SSNDetected = false;
        this.idTypeDetected = 'ITIN';
        this.registrationForm.get('idType')?.setValue('ITIN');
      } else {
        this.SSNDetected = true;
        this.ITINDetected = false;
        this.idTypeDetected = 'SSN';
        this.registrationForm.get('idType')?.setValue('SSN');
      }
    } else {
      this.SSNDetected = false;
      this.ITINDetected = false;
      this.idTypeDetected = null;
    }
  }
  
  updateDisplayedSSN() {
    const ssn = this.registrationForm.get('ssn')?.value || '';
    const digits = ssn.replace(/\D/g, '');
    
    if (this.showSSN || this.ssnFocused) {
      // Show full formatted SSN
      this.displayedSSN = ssn;
    } 
    // Mask all but last 4 digits
    else if ((!this.showSSN || !this.ssnFocused) && digits.length === 9) {
      this.displayedSSN = `***-**-${digits.substring(5)}`;
    } else if ((!this.showSSN || !this.ssnFocused) && digits.length > 5) {
      this.displayedSSN = `***-**-${digits.substring(5)}`;
    } else if (!this.showSSN || !this.ssnFocused) {
      this.displayedSSN = ssn.replace(/\d/g, '*');
    }
  }
  onSubmit() {
    if (this.registrationForm.valid) {
      const formData = { ...this.registrationForm.value };

      // Do NOT strip phone number mask here. Pass as entered (masked) for modal display.

      // Ensure idType is correctly set before submitting
      if (this.idTypeDetected) {
        formData.idType = this.idTypeDetected;

        // Set the appropriate field based on idType
        const ssnValue = formData.ssn;
        if (formData.idType === 'SSN') {
          formData.ssn = ssnValue;
          // Remove itin field if it exists
          delete formData.itin;
        } else if (formData.idType === 'ITIN') {
          formData.itin = ssnValue;
          // Remove ssn field as we're using itin
          delete formData.ssn;
        }
      }

      // Pass formData to your confirm-registration modal (phone number remains masked)
      this.openConfirmRegistrationModal(formData);
    }
  }
  openConfirmRegistrationModal(formData: any) {
    const options: NgbModalOptions = {
      backdrop: 'static',
      centered: true,
      size: 'md'
    };
    this.modalRef = this.ngModal.open(ConfirmRegistrationModalComponent, options);
    this.modalRef.componentInstance.formData = formData;
    this.modalRef.result.then((result) => {
      if (result.duplicateAccount) {
        this.duplicateAccount();
      }
    });
  }

  duplicateAccount() {
    const options: NgbModalOptions = {
      backdrop: 'static',
      centered: true,
      size: 'md'
    };
    this.modalRef = this.ngModal.open(AccountCreationModalComponent, options);
    this.modalRef.componentInstance.isSuccess = false;
    this.modalRef.componentInstance.formData = this.registrationForm.value;
    this.modalRef.componentInstance.idType = this.idTypeDetected;
    
    this.modalRef.result.then((result) => {
      if (result.forgotEmail) {
        // this.emailSentMessage = true;
        setTimeout(() => {
          // this.emailSentMessage = false;
        }, 5000);
      }
    });
  }
  goToLogin() {
    this.clearFormData();
    
    sessionStorage.clear();
    localStorage.clear();
    this.router.navigate(['']);
  }

  private closeConfirmModal() {
    if (this.modalRef) {
      this.modalRef.close();
    }
  }

  closeErrorToast() {
    this.SSNError = false;
    this.emailError = false;
    this.recoveryEmailError = false;
    this.phoneError = false;
  }

  closeSuccessToast() {
    this.emailSentMessage = false;
  }
  checkEmail() {
    let request = {
      email: this.registrationForm.get('email')?.value
    };
    if (this.registrationForm.get('email')?.value !== '' ) {
    this.registrationService.checkEmail(request).subscribe({
      next: response => {
        if (response.data.exists === true) {
          this.closeConfirmModal();
          window.scrollTo(0, 0);
          this.emailError = true;
          this.emailExists = true;
          setTimeout(() => {
            this.emailError = false;
          }, 5000);
        } else {
          this.emailError = false;
          this.emailExists = false;
        }
      }
    });
  }
  }
  checkRecoveryEmail() {
    this.validateRecoveryEmail();
    
    let request = {
      email: this.registrationForm.get('recovery_email')?.value
    };
    if (this.registrationForm.get('recovery_email')?.value !== '' ) {
    this.registrationService.checkEmail(request).subscribe({
      next: response => {
        if (response.data.exists === true) {
          this.closeConfirmModal();
          window.scrollTo(0, 0);
          this.recoveryEmailError = true;
          this.recoveryEmailExists = true;
          setTimeout(() => {
            this.recoveryEmailError = false;
          }, 5000);
        } else {
          this.recoveryEmailError = false;
          this.recoveryEmailExists = false;
        }
      }
    });
  }
  }
  checkSSN() {
    const ssnValue = this.registrationForm.get('ssn')?.value.replace(/\D/g, '');
    const idType = this.registrationForm.get('idType')?.value;
    
    let request: any = {
      idType: idType
    };
    
    if (idType === 'SSN') {
      request.ssn = ssnValue;
    } else if (idType === 'ITIN') {
      request.itin = ssnValue;
    }
    
    this.registrationService.checkSSN(request).subscribe({
      next: response => {
        if (response.data.exists === true) {
          this.closeConfirmModal();
          window.scrollTo(0, 0);
          this.SSNError = true;
          this.ssnExists = true;
          this.SSNDetected = false;
          this.ITINDetected = false;
          setTimeout(() => {
            this.SSNError = false;
          }, 5000);
        } else {
          if (this.idTypeDetected === 'SSN') {
            window.scrollTo(0, 0);
            this.SSNDetected = true;
            this.ITINDetected = false;
          } else if (this.idTypeDetected === 'ITIN') {
            window.scrollTo(0, 0);
            this.ITINDetected = true;
            this.SSNDetected = false;
          }
          this.SSNError = false;
          this.ssnExists = false;          
        }
      }
    });
  }
  checkPhone() {
    let cleanedPhoneNumber = this.registrationForm.get('phoneNumber')?.value.replace(/\D/g, '');
    let request = {
      phone: cleanedPhoneNumber
    };
    if (this.registrationForm.get('phoneNumber')?.value !== '' ) {
    this.registrationService.checkPhone(request).subscribe({
      next: response => {
        if (response.data.exists === true) {
          this.closeConfirmModal();
          window.scrollTo(0, 0);
          this.phoneError = true;
          this.phoneExists = true;
          setTimeout(() => {
            this.phoneError = false;
          }, 5000);
        } else {
          this.phoneError = false;
          this.phoneExists = false;
        }
      }
    });
  }
  }

  validateDate(control: any) {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }
    
    let selectedDate: Date;
    
    // Handle both MM/DD/YYYY and YYYY-MM-DD formats
    if (control.value.includes('/')) {
      // MM/DD/YYYY format
      const parts = control.value.split('/');
      if (parts.length !== 3) {
        return { 'futureDate': true }; // Invalid format
      }
      
      const month = parseInt(parts[0], 10);
      const day = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);
      
      // Basic validation
      if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900) {
        return { 'futureDate': true }; // Invalid date
      }
      
      selectedDate = new Date(year, month - 1, day);
      
      // Check if the date is valid (handles leap years, etc.)
      if (selectedDate.getMonth() !== month - 1 || 
          selectedDate.getDate() !== day || 
          selectedDate.getFullYear() !== year) {
        return { 'futureDate': true }; // Invalid date
      }
    } else {
      // YYYY-MM-DD format
      selectedDate = new Date(control.value);
    }
    
    const today = new Date();
    
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);
    
    if (selectedDate > today) {
      return { 'futureDate': true };
    }
    
    // Calculate age
    let age = today.getFullYear() - selectedDate.getFullYear();
    const monthDiff = today.getMonth() - selectedDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < selectedDate.getDate())) {
      age--;
    }
    if (age < this.minAge) {
      return { 'ageInvalid': true };
    }
    
    if (age > this.maxAge) {
      return { 'ageInvalid': true };
    }
    
    return null;
  }

  private loadAgeValidationRules() {
    // Fetch minimum age
    this.registrationService.getBusinessRuleByKey('Registration', 'date_of_birth_min_allowed_date')
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
      next: (response: any) => {
        if (response?.data?.data?.intValue) {
          this.minAge = response.data.data.intValue;
        }
      },
      error: (error) => {
        console.error('Error fetching minimum age rule:', error);
      }
    });

    // Fetch maximum age
    this.registrationService.getBusinessRuleByKey('Registration', 'date_of_birth_max_allowed_date')
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
      next: (response: any) => {
        console.log('Maximum age rule response:', response);
        if (response?.data?.data?.intValue) {
          this.maxAge = response.data.data.intValue;
        }
      },
      error: (error) => {
        console.error('Error fetching maximum age rule:', error);
        // Keep default value of 120
      }
    });
  }
  
  validateRecoveryEmail() {
    const emailControl = this.registrationForm?.get('email');
    const recoveryEmailControl = this.registrationForm?.get('recovery_email');
    
    if (!emailControl || !recoveryEmailControl) {
      return null;
    }
    
    const email = emailControl.value;
    const recoveryEmail = recoveryEmailControl.value;
    
    if (recoveryEmail && email && recoveryEmail.toLowerCase() === email.toLowerCase()) {
      recoveryEmailControl.setErrors({ 'sameAsEmail': true });
      return { 'sameAsEmail': true };
    } else if (recoveryEmailControl.errors?.['sameAsEmail']) {
      // If the emails are no longer the same, clear only the sameAsEmail error
      const currentErrors = {...recoveryEmailControl.errors};
      delete currentErrors['sameAsEmail'];
      
      // If no errors left, set to null, otherwise keep other validation errors
      recoveryEmailControl.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
    }
    
    return null;
  }

  clearFormData() {
    this.registrationForm.reset({
      firstName: '',
      middleName: '',
      lastName: '',
      suffix: null,
      dob: '',
      gender: null,
      race: null,
      ethnicity: null,
      ssn: '',
      idType: 'SSN',
      email: '',
      recovery_email: '',
      phoneNumber: '',
      terms: false,
      consent: false
    });

    this.ssnExists = false;
    this.emailExists = false;
    this.phoneExists = false;
    this.recoveryEmailExists = false;
    this.SSNError = false;
    this.emailError = false;
    this.recoveryEmailError = false;
    this.phoneError = false;
    this.SSNDetected = false;
    this.ITINDetected = false;
    this.idTypeDetected = null;
    this.emailSentMessage = false;
    this.showSSN = true;
    this.ssnFocused = false;
    this.displayedSSN = '';
    this.isEmailFocused = false;
    this.isPhoneFocused = false;
    this.isRecoveryEmailFocused = false;
    this.isSSNFocused = false;
    this.showSSNTooltip = false;
    this.emailTooltip = false;
    this.recoveryEmailTooltip = false;

    this.registrationForm.markAsUntouched();
    this.registrationForm.markAsPristine();
    
  }
  get firstName() {
    return this.registrationForm.get('firstName');
  }
  get middleName() {
    return this.registrationForm.get('middleName');
  }
  get lastName() {
    return this.registrationForm.get('lastName');
  }
  get suffix() {
    return this.registrationForm.get('suffix');
  }
  get dob() {
    return this.registrationForm.get('dob');
  }
  get gender() {
    return this.registrationForm.get('gender');
  }
  get race() {
    return this.registrationForm.get('race');
  }
  get ethnicity() {
    return this.registrationForm.get('ethnicity');
  }
  get ssn() {
    return this.registrationForm.get('ssn');
  }
  get email() {
    return this.registrationForm.get('email');
  }
  get recovery_email() {
    return this.registrationForm.get('recovery_email');
  }
  get phoneNumber() {
    return this.registrationForm.get('phoneNumber');
  }
  open(content) {
    this.ngModal.open(content, { size: 'md', backdrop: 'static' }).result.then(
      result => {
        this.closeResult = `Closed with: ${result}`;
      },
      reason => {
        this.closeResult = `Dismissed ${this.getDismissReason(reason)}`;
      }
    );
  }
  private getDismissReason(reason: any): string {
    if (reason === ModalDismissReasons.ESC) {
      return 'by pressing ESC';
    } else if (reason === ModalDismissReasons.BACKDROP_CLICK) {
      return 'by clicking on a backdrop';
    } else {
      return `with: ${reason}`;
    }
  }

  openTermsAndConditionsModal(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.userAgreementContent) {
      this.showTermsAndConditionsModal();
      return;
    }

    
    this.registrationService.getUserAgreementContent(500, '4', 'EDUCATOR').subscribe({
      next: (response: any) => {
        if (response?.data?.length > 0) {
          const optionalData = response.data[0].optionaldata;
          if (optionalData) {
            try {
              const parsed = typeof optionalData === 'string' ? JSON.parse(optionalData) : optionalData;
              this.userAgreementContent = parsed?.content || '';
            } catch {
              this.userAgreementContent = optionalData;
            }
          }
        }
        this.showTermsAndConditionsModal();
      },
      error: err => {
        console.error('Error loading user agreement content:', err);
        this.showTermsAndConditionsModal();
      }
    });
  }

  private showTermsAndConditionsModal(): void {
    this.matDialog.open(ContentManagementModalComponent, {
      width: '900px',
      disableClose: true,
      data: {
        heading: modalHeading.tnc,
        content: this.userAgreementContent,
        buttons: [{ text: modalButtonTitles.close, class: 'btn-outline-primary' }]
      }
    });
  }

  openPrivacyPolicyModal(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    
    const options: NgbModalOptions = {
      backdrop: "static",
      centered: true,
      size: "lg"
    };
    
    this.customModal = this.ngModal.open(ModalComponent, options);
    
    const modalConfig: ModalConfig = {
      buttons: [
        { text: 'Close', code: 'N', btnOutline: false }
      ],
      heading: 'Privacy Policy',
      modalContent: DOCUMENT_CONTENT.PRIVACY_POLICY,
      hasTemplate: true,
      scrollable: true
    };
    
    this.customModal.componentInstance.modalConfig = modalConfig;
  }


}
